import React, { FunctionComponent, useEffect, useState } from 'react';
import { TradeIn } from '@upstart/harmony/lib/legos/dealTypes';
import validation from '@upstart/harmony/lib/validation';
import { MessageType } from '@upstart/harmony/lib/legos/websiteTypes';
import { sleep } from '@upstart/harmony/lib/legos/utils/tsutils';
import './TradeInsTableRow.scss';
import { FAUX_TRADE_IN_ID } from '@upstart/harmony/lib/legos/constants';
import { keyBy } from 'lodash';
import { getTradeInAllowance } from '@upstart/harmony/lib/dealer-utils';
import classNames from 'classnames';
import Input from '../Input';
import DecodeService from '../../../services/DecodeService';
import KbbService, { IdNamePair } from '../../../services/KbbService';
import { PageMessage } from '../../GlobalUI';
import Select from '../../PencilEditor/Select';
import {
  getTradeInPayoffState,
  INPUT_DISABLED_BY_LOSS_EXPRESS_TOOLTIP,
  TradeInStatusMap,
} from '../../../util/tradePayoffUtils';
import Tooltip from '../../Tooltip/Tooltip';

export type TradeInFieldKey =
  | 'id'
  | 'year'
  | 'make'
  | 'model'
  | 'payOff'
  | 'tradeValue'
  | 'tradeInAllowance'
  | 'trim'
  | 'mileage';
export interface TradeInField {
  key: keyof Pick<TradeIn, TradeInFieldKey>;
  isEditable: boolean;
  required?: boolean;
}

const TradeInHeaderMapping: Record<TradeInFieldKey, string> = {
  id: 'VIN',
  tradeValue: 'Value',
  trim: 'Trim',
  make: 'Make',
  year: 'Year',
  model: 'Model',
  payOff: 'Payoff',
  mileage: 'Mileage',
  tradeInAllowance: 'Allowance',
};

export interface TradeInsTableRowProps {
  tradeIn: TradeIn;
  fields: TradeInField[];
  index: number;
  years: string[];
  dealerId: string;
  onSave: (tradeIn: TradeIn, index: number) => void;
  onDelete: (index: number) => void;
  setIsLoading: (state: boolean) => void;
  canEditTradeIns?: boolean;
  canDeleteTradeIns?: boolean;
  validationError?: TradeInError;
}

export const TradeInsTableRow: FunctionComponent<TradeInsTableRowProps> = ({
  tradeIn,
  fields,
  index,
  years,
  dealerId,
  onSave,
  onDelete,
  setIsLoading,
  validationError,
  canEditTradeIns,
  canDeleteTradeIns,
}) => {
  const [vin, setVin] = useState<string>('');
  const [year, setYear] = useState<string>('');
  const [make, setMake] = useState<string>('');
  const [model, setModel] = useState<string>('');
  const [trim, setTrim] = useState<string>('');
  const [mileage, setMileage] = useState<number | undefined>(undefined);
  const [tradeValue, setTradeValue] = useState<number | undefined>(undefined);
  const [allowance, setAllowance] = useState<number | undefined | null>(
    undefined,
  );

  const fieldConfig = keyBy(fields, 'key');
  const includeHeaderRow = index === 0;

  const [payOff, setPayOff] = useState<number | undefined>(undefined);

  const [makes, setMakes] = useState<string[]>([]);
  const [models, setModels] = useState<string[]>([]);
  const [trims, setTrims] = useState<string[]>([]);

  const lossExpressState = getTradeInPayoffState(tradeIn);
  const lossExpressProps = TradeInStatusMap[lossExpressState];
  const tooltip = lossExpressProps.inputDisabled
    ? INPUT_DISABLED_BY_LOSS_EXPRESS_TOOLTIP
    : '';

  useEffect(() => {
    setVin(tradeIn.id && tradeIn.id !== FAUX_TRADE_IN_ID ? tradeIn.id : '');
    setYear(tradeIn.year?.name ? tradeIn.year?.name.toString() : '');
    setMake(tradeIn.make?.name ?? '');
    setModel(tradeIn.model?.name ?? '');
    setTrim(tradeIn.trim?.name ?? '');
    setMileage(tradeIn.mileage ? +tradeIn.mileage : undefined);
    setTradeValue(
      (tradeIn.tradeValue ?? tradeIn.tradeInAllowance) || undefined,
    );
    setPayOff(tradeIn.payOff ? +tradeIn.payOff : undefined);
    setAllowance(getTradeInAllowance([tradeIn]));
  }, [tradeIn]);

  const saveData = (): void => {
    const updatedTradeIn = { ...tradeIn };
    updatedTradeIn.id = vin !== '' ? vin : undefined;
    updatedTradeIn.year = year ? { name: year } : undefined;
    updatedTradeIn.make = make ? { name: make } : undefined;
    updatedTradeIn.model = model ? { name: model } : undefined;
    updatedTradeIn.trim = trim ? { name: trim } : undefined;
    updatedTradeIn.mileage = mileage ? mileage.toString() : undefined;
    updatedTradeIn.tradeValue = tradeValue;
    updatedTradeIn.payOff = payOff ?? 0;
    updatedTradeIn.tradeInAllowance = allowance ?? 0;
    onSave(updatedTradeIn, index);
  };

  const processVin = (newVin: string) => {
    if (validation.isVin(`${newVin}`.toUpperCase())) {
      const decodeVin = async () => {
        try {
          setIsLoading(true);
          const {
            year: yearFromVin,
            make: makeFromVin,
            model: modelFromVin,
            trim: trimFromVin,
          } = await DecodeService.decodeVin(newVin ?? '', dealerId);
          setYear(
            yearFromVin !== undefined && yearFromVin !== null
              ? yearFromVin.toString()
              : '',
          );
          setMake(makeFromVin ?? '');
          setModel(modelFromVin ?? '');
          setTrim(trimFromVin ?? '');
          setIsLoading(false);
        } catch (err) {
          setIsLoading(false);
          PageMessage({
            type: MessageType.ERROR,
            message: 'Unable to decode VIN',
            headline: 'Error',
          });
        }
      };
      decodeVin().catch(() => {});
    }
  };

  const MAX_TRIES = 3;

  // generic function to fetch a list from a kbb endpoint
  const fetchList = async (
    fetchFn: (...args: string[]) => Promise<IdNamePair[]>,
    args: string[],
    setter: (value: React.SetStateAction<string[]>) => void,
  ): Promise<void> => {
    let attempt = 0;
    setIsLoading(true);
    // the kbb service sometimes returns an error, so we need to retry
    while (attempt < MAX_TRIES) {
      try {
        attempt += 1;
        setter((await fetchFn(...args)).map(p => p.name));
        break;
      } catch (err) {
        if (attempt >= MAX_TRIES) {
          PageMessage({
            type: MessageType.ERROR,
            message: 'Unable to fetch data from server',
            headline: 'Error',
          });
        } else {
          // wait for a little bit before retrying
          await sleep(Math.random() * 2000);
        }
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (year) {
      fetchList(KbbService.getMakes, [year], setMakes).catch(() => {});
    } else {
      setMakes([]);
    }
  }, [year]);

  useEffect(() => {
    if (year && make) {
      fetchList(KbbService.getModels, [year, make], setModels).catch(() => {});
    } else {
      setModels([]);
    }
  }, [make]);

  useEffect(() => {
    if (year && make && model) {
      fetchList(
        KbbService.getTrims,
        [year, make, model],
        setTrims,
      ).catch(() => {});
    } else {
      setTrims([]);
    }
  }, [model]);

  useEffect(() => {
    saveData();
  }, [vin, year, make, model, trim, mileage, tradeValue, payOff, allowance]);

  const renderSelect = ({
    list,
    selectedValue,
    errorMsg,
    setter,
    childSetters,
    disabled,
    headerText,
    fieldName,
  }: {
    list: string[];
    selectedValue: string;
    errorMsg?: string | undefined;
    setter: (value: string) => void;
    childSetters?: ((value: string) => void)[];
    disabled?: boolean;
    headerText?: string;
    fieldName?: string;
  }): JSX.Element => {
    const optionList =
      list.includes(selectedValue) || list.length === 0
        ? list
        : [selectedValue, ...list];
    return (
      <div
        className={classNames(
          'fieldContainer',
          fieldName === fieldConfig.year.key ? 'number-field' : '',
        )}
      >
        {includeHeaderRow && <div className="headerText">{headerText}</div>}
        <Select
          className={errorMsg ? 'uiInputError' : 'uiInput'}
          value={selectedValue}
          onChange={(newValue: string) => {
            setter(newValue);
            if (childSetters) {
              childSetters.forEach(childSetter => childSetter(''));
            }
          }}
          disabled={!canEditTradeIns || disabled}
          data-testid={`${index}-${fieldName}`}
        >
          {optionList.map(itemValue => (
            <Select.Option key={itemValue} value={itemValue} name={itemValue} />
          ))}
        </Select>
        {errorMsg && (
          <div
            className="errorMessage"
            data-testid={`${index}-${fieldName}-errorMessage`}
          >
            {errorMsg}
          </div>
        )}
      </div>
    );
  };

  const getNumericValue = (
    value: string,
    decimalPlaces = 2,
  ): number | undefined => {
    const filteredValue = value.replace(/[^0-9.]/gi, '');
    const number = Number(filteredValue);
    return Number.isNaN(number)
      ? undefined
      : Number(number.toFixed(decimalPlaces));
  };

  return (
    <div className="rowContainer">
      {fieldConfig.id && (
        <div className="fieldContainer vin-field">
          {includeHeaderRow && (
            <div className="headerText">
              {TradeInHeaderMapping[fieldConfig.id.key]}
            </div>
          )}
          <div>
            <Input
              className={validationError?.vin ? 'uiInputError' : 'uiInput'}
              data-testid={`${index}-vin`}
              type="text"
              value={vin}
              maxLength={17}
              onChange={newValue => {
                const filteredVin = newValue
                  .toString()
                  .replace(/[^a-z0-9]/gi, '');
                setVin(filteredVin);
                processVin(filteredVin);
              }}
              disabled={!canEditTradeIns || !fieldConfig.id.isEditable}
            />
          </div>
          {validationError?.vin && (
            <div
              className="errorMessage"
              data-testid={`${index}-vin-errorMessage`}
            >
              {validationError?.vin}
            </div>
          )}
        </div>
      )}
      {fieldConfig.year &&
        renderSelect({
          list: years,
          selectedValue: year,
          errorMsg: validationError?.year,
          setter: setYear,
          childSetters: [setMake, setModel, setTrim],
          disabled: !fieldConfig.year.isEditable,
          headerText: TradeInHeaderMapping[fieldConfig.year.key],
          fieldName: fieldConfig.year.key,
        })}
      {fieldConfig.make &&
        renderSelect({
          list: makes,
          selectedValue: make,
          errorMsg: validationError?.make,
          setter: setMake,
          childSetters: [setModel, setTrim],
          disabled: !fieldConfig.make.isEditable,
          headerText: TradeInHeaderMapping[fieldConfig.make.key],
          fieldName: fieldConfig.make.key,
        })}
      {fieldConfig.model &&
        renderSelect({
          list: models,
          selectedValue: model,
          errorMsg: validationError?.model,
          setter: setModel,
          childSetters: [setTrim],
          disabled: !fieldConfig.model.isEditable,
          headerText: TradeInHeaderMapping[fieldConfig.model.key],
          fieldName: fieldConfig.model.key,
        })}
      {fieldConfig.trim &&
        renderSelect({
          list: trims,
          selectedValue: trim,
          setter: setTrim,
          disabled: !fieldConfig.model.isEditable,
          headerText: TradeInHeaderMapping[fieldConfig.trim.key],
          fieldName: fieldConfig.trim.key,
        })}
      {fieldConfig.mileage && (
        <div className="fieldContainer number-field">
          {includeHeaderRow && (
            <div className="headerText">
              {TradeInHeaderMapping[fieldConfig.mileage.key]}
            </div>
          )}
          <div className="priceContainer">
            <Input
              className={validationError?.mileage ? 'uiInputError' : 'uiInput'}
              data-testid={`${index}-mileage`}
              type="text"
              value={mileage}
              maxLength={8}
              onChange={newValue => {
                setMileage(getNumericValue(newValue.toString(), 0));
              }}
              disabled={!canEditTradeIns || !fieldConfig.mileage.isEditable}
            />
          </div>
          {validationError?.mileage && (
            <div
              className="errorMessage"
              data-testid={`${index}-mileage-errorMessage`}
            >
              {validationError?.mileage}
            </div>
          )}
        </div>
      )}
      {fieldConfig.tradeInAllowance && (
        <div className="fieldContainer number-field">
          {includeHeaderRow && (
            <div className="headerText">
              {TradeInHeaderMapping[fieldConfig.tradeInAllowance.key]}
            </div>
          )}
          <div className="priceContainer">
            <div>$</div>
            <Input
              className={
                validationError?.allowance ? 'uiInputError' : 'uiInput'
              }
              type="text"
              value={allowance}
              data-testid={`${index}-allowance`}
              maxLength={8}
              onChange={newValue =>
                setAllowance(getNumericValue(newValue.toString()))
              }
              placeholder="0"
              disabled={
                !canEditTradeIns || !fieldConfig.tradeInAllowance.isEditable
              }
            />
          </div>
          {validationError?.value && (
            <div
              className="errorMessageExtended"
              data-testid={`${index}-allowance-errorMessage`}
            >
              {validationError?.value}
            </div>
          )}
        </div>
      )}
      {fieldConfig.tradeValue && (
        <div className="fieldContainer number-field">
          {includeHeaderRow && (
            <div className="headerText">
              {TradeInHeaderMapping[fieldConfig.tradeValue.key]}
            </div>
          )}
          <div className="priceContainer">
            <div>$</div>
            <Input
              className={validationError?.value ? 'uiInputError' : 'uiInput'}
              type="text"
              value={tradeValue}
              data-testid={`${index}-value`}
              maxLength={8}
              onChange={newValue =>
                setTradeValue(getNumericValue(newValue.toString()))
              }
              placeholder="0"
              disabled={!canEditTradeIns || !fieldConfig.tradeValue.isEditable}
            />
          </div>
          {validationError?.value && (
            <div
              className="errorMessageExtended"
              data-testid={`${index}-value-errorMessage`}
            >
              {validationError?.value}
            </div>
          )}
        </div>
      )}
      {fieldConfig.payOff && (
        <div className="fieldContainer number-field">
          {includeHeaderRow && (
            <div className="headerText">
              {TradeInHeaderMapping[fieldConfig.payOff.key]}
            </div>
          )}
          <div className="priceContainer">
            <div>$</div>
            {lossExpressProps.inputDisabled && tooltip && (
              <Tooltip text={tooltip} />
            )}
            <Input
              className="uiInput"
              type="text"
              value={payOff}
              data-testid={`${index}-payoff`}
              maxLength={8}
              onChange={newValue => {
                setPayOff(getNumericValue(newValue.toString()));
              }}
              placeholder="0"
              disabled={
                !canEditTradeIns ||
                !fieldConfig.payOff.isEditable ||
                lossExpressProps.inputDisabled
              }
            />
          </div>
        </div>
      )}
      {canDeleteTradeIns && (
        <div
          className={classNames(
            'deleteIcon',
            includeHeaderRow ? 'add-header-offset' : '',
          )}
          onClick={() => onDelete(index)}
          data-testid={`${index}-deleteBtn`}
        >
          ✕
        </div>
      )}
    </div>
  );
};

export interface TradeInError {
  vin?: string;
  year?: string;
  make?: string;
  model?: string;
  mileage?: string;
  value?: string;
  allowance?: string;
}

export interface ErrorMap {
  [index: number]: TradeInError;
}
